/**
/*
 * Showtime V2 - Search JavaScript
 *
 * Search-specific functionality:
 * - Header search toggle
 * - Predictive search (AJAX suggestions)
 *
 * Only loaded on pages that need search functionality.
 * Progressive enhancement only - core functionality works without JS.
 */

(function() {
  'use strict';

  // Get utilities from global scope
  const { debounce, trapFocus } = window.ShowtimeTheme || {};

  // Fallback debounce if global not available
  const _debounce = debounce || function(fn, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        fn(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // Fallback trapFocus if global not available
  const _trapFocus = trapFocus || function(element) {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    function handleKeyDown(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          lastFocusable.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          firstFocusable.focus();
          e.preventDefault();
        }
      }
    }
    element.addEventListener('keydown', handleKeyDown);
    return () => element.removeEventListener('keydown', handleKeyDown);
  };

  /**
   * Header Search Toggle
   * Handles expandable search panel in header
   */
  class HeaderSearch {
    constructor() {
      this.container = document.querySelector('[data-header-search]');
      if (!this.container) return;

      this.toggleBtn = this.container.querySelector('[data-search-toggle]');
      this.panel = this.container.querySelector('[data-search-panel]');
      this.input = this.container.querySelector('[data-predictive-search-input]');
      this.closeBtn = this.container.querySelector('[data-search-close]');

      if (!this.toggleBtn || !this.panel) return;

      this.isOpen = false;
      this.removeFocusTrap = null;
      this.init();
    }

    init() {
      // Toggle button click
      this.toggleBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggle();
      });

      // Close button click
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      // Close on Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });

      // Close on click outside
      document.addEventListener('click', (e) => {
        if (this.isOpen && !this.container.contains(e.target)) {
          this.close();
        }
      });
    }

    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      this.isOpen = true;
      this.panel.hidden = false;
      this.toggleBtn.setAttribute('aria-expanded', 'true');
      this.container.classList.add('search-open');

      // Focus input after transition
      requestAnimationFrame(() => {
        if (this.input) {
          this.input.focus();
          this.input.select();
        }
      });

      // Trap focus within panel
      this.removeFocusTrap = _trapFocus(this.panel);
    }

    close() {
      this.isOpen = false;
      this.panel.hidden = true;
      this.toggleBtn.setAttribute('aria-expanded', 'false');
      this.container.classList.remove('search-open');

      // Clear input
      if (this.input) {
        this.input.value = '';
        // Hide results
        const resultsContainer = this.container.querySelector('[data-predictive-search-results]');
        if (resultsContainer) {
          resultsContainer.hidden = true;
          resultsContainer.innerHTML = '';
        }
      }

      // Remove focus trap
      if (this.removeFocusTrap) {
        this.removeFocusTrap();
        this.removeFocusTrap = null;
      }

      // Return focus to toggle
      this.toggleBtn.focus();
    }
  }

  /**
   * Predictive Search
   * Fetches and displays search suggestions using Shopify's native predictive search endpoint.
   * Uses section rendering for proper server-side rendering of results.
   */
  class PredictiveSearch {
    constructor() {
      this.forms = document.querySelectorAll('[data-predictive-search-form]');
      if (!this.forms.length) return;

      this.init();
    }

    init() {
      this.forms.forEach(form => {
        const input = form.querySelector('[data-predictive-search-input]');
        const resultsContainer = form.querySelector('[data-predictive-search-results]');

        if (!input || !resultsContainer) return;

        // Get predictive search URL from form data attribute
        const predictiveSearchUrl = form.dataset.predictiveSearchUrl || '/search/suggest';

        const instance = {
          form,
          input,
          resultsContainer,
          predictiveSearchUrl,
          cachedResults: {},
          selectedIndex: -1,
          isOpen: false,
          abortController: null
        };

        // Debounced search
        const debouncedSearch = _debounce((query) => {
          this.search(instance, query);
        }, 300);

        // Input events
        input.addEventListener('input', () => {
          const query = input.value.trim();
          if (query.length >= 2) {
            debouncedSearch(query);
          } else {
            this.closeResults(instance);
          }
        });

        // Keyboard navigation
        input.addEventListener('keydown', (e) => {
          this.handleKeydown(instance, e);
        });

        // Close on blur (with delay for click handling)
        input.addEventListener('blur', () => {
          setTimeout(() => {
            if (!resultsContainer.contains(document.activeElement)) {
              this.closeResults(instance);
            }
          }, 200);
        });

        // Re-open on focus if there's content
        input.addEventListener('focus', () => {
          const query = input.value.trim();
          if (query.length >= 2 && resultsContainer.innerHTML.trim()) {
            this.openResults(instance);
          }
        });

        // Handle result clicks
        resultsContainer.addEventListener('click', (e) => {
          const link = e.target.closest('a');
          if (link) {
            // Allow natural navigation
            this.closeResults(instance);
          }
        });
      });
    }

    escapeHtml(str) {
      if (str === null || str === undefined) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    formatMoney(value) {
      if (value === null || value === undefined) return '';
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && typeof value === 'number') {
        return window.Shopify.formatMoney(value);
      }
      if (typeof value === 'string') return value;
      if (typeof value === 'number') return `$${(value / 100).toFixed(2)}`;
      return '';
    }

    buildResultsFromJson(data, query) {
      const resources = data && data.resources && data.resources.results ? data.resources.results : {};
      const productsAll = Array.isArray(resources.products) ? resources.products : [];
      const products = productsAll.filter((product) => product && product.available !== false);
      const collections = Array.isArray(resources.collections) ? resources.collections : [];
      const pages = Array.isArray(resources.pages) ? resources.pages : [];
      const articles = Array.isArray(resources.articles) ? resources.articles : [];

      const hasResults = products.length || collections.length || pages.length || articles.length;
      if (!hasResults) {
        return `<div class="predictive-search__no-results"><p>No results for "${this.escapeHtml(query)}"</p></div>`;
      }

      let html = '<div class="predictive-search__results">';

      if (products.length) {
        html += '<div class="predictive-search__group" data-result-group="products">';
        html += '<h3 class="predictive-search__group-heading">Products</h3>';
        html += '<ul class="predictive-search__list" role="listbox">';
        products.forEach((product, index) => {
          const title = this.escapeHtml(product.title || '');
          const url = this.escapeHtml(product.url || '#');
          const image = product.image ? this.escapeHtml(product.image) : '';
          const price = this.formatMoney(product.price);
          const compare = this.formatMoney(product.compare_at_price);
          html += `<li class="predictive-search__item" role="option" id="predictive-search-option-product-${index + 1}" data-predictive-search-item>`;
          html += `<a href="${url}" class="predictive-search__link" tabindex="-1">`;
          if (image) {
            html += `<div class="predictive-search__image-wrapper"><img src="${image}" alt="${title}" class="predictive-search__image" width="50" height="50" loading="lazy"></div>`;
          } else {
            html += `<div class="predictive-search__image-wrapper predictive-search__image-wrapper--placeholder"></div>`;
          }
          html += `<div class="predictive-search__item-content"><span class="predictive-search__item-title">${title}</span>`;
          if (price) {
            html += `<span class="predictive-search__item-price">`;
            if (compare && compare !== price) {
              html += `<span class="predictive-search__item-price--sale">${price}</span>`;
              html += `<s class="predictive-search__item-price--compare">${compare}</s>`;
            } else {
              html += `${price}`;
            }
            html += `</span>`;
          }
          html += `</div></a></li>`;
        });
        html += '</ul></div>';
      }

      if (collections.length) {
        html += '<div class="predictive-search__group" data-result-group="collections">';
        html += '<h3 class="predictive-search__group-heading">Collections</h3>';
        html += '<ul class="predictive-search__list" role="listbox">';
        collections.forEach((collection, index) => {
          const title = this.escapeHtml(collection.title || '');
          const url = this.escapeHtml(collection.url || '#');
          const image = collection.image ? this.escapeHtml(collection.image) : '';
          const count = collection.products_count ? this.escapeHtml(collection.products_count) : '';
          html += `<li class="predictive-search__item predictive-search__item--collection" role="option" id="predictive-search-option-collection-${index + 1}" data-predictive-search-item>`;
          html += `<a href="${url}" class="predictive-search__link" tabindex="-1">`;
          if (image) {
            html += `<div class="predictive-search__image-wrapper"><img src="${image}" alt="${title}" class="predictive-search__image" width="50" height="50" loading="lazy"></div>`;
          }
          html += `<div class="predictive-search__item-content${image ? '' : ' predictive-search__item-content--no-image'}"><span class="predictive-search__item-title">${title}</span>`;
          if (count) {
            html += `<span class="predictive-search__item-count">${count} products</span>`;
          }
          html += `</div></a></li>`;
        });
        html += '</ul></div>';
      }

      if (pages.length) {
        html += '<div class="predictive-search__group" data-result-group="pages">';
        html += '<h3 class="predictive-search__group-heading">Pages</h3>';
        html += '<ul class="predictive-search__list" role="listbox">';
        pages.forEach((page, index) => {
          const title = this.escapeHtml(page.title || '');
          const url = this.escapeHtml(page.url || '#');
          html += `<li class="predictive-search__item predictive-search__item--page" role="option" id="predictive-search-option-page-${index + 1}" data-predictive-search-item>`;
          html += `<a href="${url}" class="predictive-search__link" tabindex="-1">`;
          html += `<div class="predictive-search__item-content"><span class="predictive-search__item-title">${title}</span></div>`;
          html += `</a></li>`;
        });
        html += '</ul></div>';
      }

      if (articles.length) {
        html += '<div class="predictive-search__group" data-result-group="articles">';
        html += '<h3 class="predictive-search__group-heading">Articles</h3>';
        html += '<ul class="predictive-search__list" role="listbox">';
        articles.forEach((article, index) => {
          const title = this.escapeHtml(article.title || '');
          const url = this.escapeHtml(article.url || '#');
          const image = article.image ? this.escapeHtml(article.image) : '';
          html += `<li class="predictive-search__item predictive-search__item--article" role="option" id="predictive-search-option-article-${index + 1}" data-predictive-search-item>`;
          html += `<a href="${url}" class="predictive-search__link" tabindex="-1">`;
          if (image) {
            html += `<div class="predictive-search__image-wrapper"><img src="${image}" alt="${title}" class="predictive-search__image" width="50" height="50" loading="lazy"></div>`;
          }
          html += `<div class="predictive-search__item-content${image ? '' : ' predictive-search__item-content--no-image'}"><span class="predictive-search__item-title">${title}</span></div>`;
          html += `</a></li>`;
        });
        html += '</ul></div>';
      }

      html += '</div>';
      return html;
    }

    async search(instance, query) {
      const { input, resultsContainer, predictiveSearchUrl, cachedResults } = instance;

      // Check cache first
      if (cachedResults[query]) {
        this.renderResults(instance, cachedResults[query]);
        return;
      }

      // Abort previous request if any
      if (instance.abortController) {
        instance.abortController.abort();
      }
      instance.abortController = new AbortController();

      // Show loading state
      resultsContainer.innerHTML = '<div class="predictive-search__loading">Loading...</div>';
      this.openResults(instance);

      try {
        // Build URL using Shopify's predictive search endpoint with section rendering
        const url = `${predictiveSearchUrl}?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article&resources[limit]=10&section_id=predictive-search`;

        const response = await fetch(url, {
          signal: instance.abortController.signal
        });

        if (!response.ok) throw new Error('Search failed');

        const text = await response.text();
        const trimmed = text.trim();

        // Parse the section HTML from response
        let sectionHtml = '';
        if (trimmed.startsWith('{')) {
          try {
            const jsonData = JSON.parse(trimmed);
            sectionHtml = this.buildResultsFromJson(jsonData, query);
          } catch (e) {
            sectionHtml = '';
          }
        } else {
          const parser = new DOMParser();
          const doc = parser.parseFromString(text, 'text/html');
          const sectionContent = doc.querySelector('.shopify-section');
          sectionHtml = sectionContent ? sectionContent.innerHTML : text;

          // If the HTML doesn't include predictive search markup, try JSON fallback
          if (!sectionHtml || !sectionHtml.match(/predictive-search__(results|no-results|group|item)/)) {
            try {
              const jsonUrl = predictiveSearchUrl.endsWith('.json')
                ? `${predictiveSearchUrl}?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article&resources[limit]=10`
                : `${predictiveSearchUrl}.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article&resources[limit]=10`;
              const jsonResponse = await fetch(jsonUrl, { signal: instance.abortController.signal });
              if (jsonResponse.ok) {
                const jsonData = await jsonResponse.json();
                sectionHtml = this.buildResultsFromJson(jsonData, query);
              }
            } catch (e) {
              // Ignore JSON fallback errors and let sectionHtml remain as-is
            }
          }
        }

        // Cache the result
        cachedResults[query] = sectionHtml;

        // Only render if query still matches (user may have typed more)
        if (input.value.trim() === query) {
          this.renderResults(instance, sectionHtml);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          // Request was aborted, ignore
          return;
        }
        console.error('Predictive search error:', error);
        resultsContainer.innerHTML = '<div class="predictive-search__error">Search unavailable</div>';
      }
    }

    renderResults(instance, html) {
      const { resultsContainer, input } = instance;

      // Inject results
      resultsContainer.innerHTML = html;

      // Reset selection
      instance.selectedIndex = -1;
      input.removeAttribute('aria-activedescendant');

      // Open if not already
      this.openResults(instance);
    }

    openResults(instance) {
      const { input, resultsContainer } = instance;

      // Don't open if no content
      if (!resultsContainer.innerHTML.trim()) return;

      resultsContainer.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      instance.isOpen = true;
    }

    closeResults(instance) {
      const { input, resultsContainer } = instance;
      resultsContainer.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      instance.isOpen = false;
      instance.selectedIndex = -1;
      input.removeAttribute('aria-activedescendant');
    }

    handleKeydown(instance, e) {
      const { input, resultsContainer, form } = instance;

      // If not open, let Enter submit the form normally
      if (!instance.isOpen) {
        if (e.key === 'Escape') {
          // Close the entire search panel if open
          const headerSearch = input.closest('[data-header-search]');
          if (headerSearch) {
            const closeBtn = headerSearch.querySelector('[data-search-close]');
            if (closeBtn) closeBtn.click();
          }
        }
        return;
      }

      const items = resultsContainer.querySelectorAll('[data-predictive-search-item]');
      if (!items.length && e.key !== 'Escape') return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          instance.selectedIndex = Math.min(instance.selectedIndex + 1, items.length - 1);
          this.updateSelection(instance, items);
          break;

        case 'ArrowUp':
          e.preventDefault();
          instance.selectedIndex = Math.max(instance.selectedIndex - 1, -1);
          this.updateSelection(instance, items);
          break;

        case 'Enter':
          if (instance.selectedIndex >= 0 && items[instance.selectedIndex]) {
            e.preventDefault();
            const link = items[instance.selectedIndex].querySelector('a');
            if (link) {
              link.click();
            }
          }
          // If no item selected, let form submit naturally
          break;

        case 'Escape':
          e.preventDefault();
          this.closeResults(instance);
          input.focus();
          break;
      }
    }

    updateSelection(instance, items) {
      const { input } = instance;

      // Remove previous selection
      items.forEach(item => {
        item.classList.remove('is-selected');
        item.setAttribute('aria-selected', 'false');
      });

      // Add new selection
      if (instance.selectedIndex >= 0 && items[instance.selectedIndex]) {
        const selectedItem = items[instance.selectedIndex];
        selectedItem.classList.add('is-selected');
        selectedItem.setAttribute('aria-selected', 'true');
        selectedItem.scrollIntoView({ block: 'nearest' });

        // Update input aria-activedescendant
        const itemId = selectedItem.id || `predictive-search-item-${instance.selectedIndex}`;
        selectedItem.id = itemId;
        input.setAttribute('aria-activedescendant', itemId);
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    }
  }

  /**
   * Initialize search components when DOM is ready
   */
  function init() {
    // Header search toggle
    new HeaderSearch();

    // Predictive search
    new PredictiveSearch();
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
